import { PhoneCallbackOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import type { TranslationKey } from '@/i18n/translation-key';
import type { FlowContributorStatusIconColor } from '@/utils/contributor-extraction.utils';
import {
  contributorRemoteSpeakerStatusIconTooltip,
  contributorVisibilityActionLabel,
  type ContributorItemLabelContext,
} from '@/components/events/detail/tabs/common/contributors/contributor-item-action-labels';

function toMuiIconColor(color: FlowContributorStatusIconColor): 'primary' | 'info' | 'warning' | 'success' {
  if (color === 'primary') return 'primary';
  if (color === 'warning') return 'warning';
  if (color === 'success') return 'success';
  return 'info';
}

const statusIconButtonSx = {
  '& > svg': {
    fontSize: '1.5rem',
    width: '1.5rem',
    height: '1.5rem',
  },
} as const;

/** Keep layout identical to clickable IconButtons when the status icon is display-only. */
const nonInteractiveIconButtonSx = {
  ...statusIconButtonSx,
  'cursor': 'default',
  '&:hover': { backgroundColor: 'transparent' },
} as const;

export type ContributorItemInlineStatusIconsProps = {
  t: (key: TranslationKey) => string;
  labelContext?: ContributorItemLabelContext;
  showRemoteSpeaker?: boolean;
  remoteSpeakerEnabled?: boolean;
  remoteSpeakerIconColor?: FlowContributorStatusIconColor;
  /** Pending day entitlement — opens quota / contact modal. */
  onRemoteSpeakerStatusClick?: () => void;
  remoteSpeakerStatusClickable?: boolean;
  showVisibility?: boolean;
  visibilityEnabled?: boolean;
  onToggleVisibility?: () => void;
  remoteSpeakerLoading?: boolean;
  visibilityLoading?: boolean;
  disabled?: boolean;
};

/**
 * Status icons for the row header — opposite of the overflow menu action icons.
 * Remote ON → PhoneCallbackOutlined (menu toggles in-person). Pending day → icon opens quota / contact modal.
 */
export function contributorItemInlineStatusIcons({
  t,
  labelContext = 'session',
  showRemoteSpeaker = false,
  remoteSpeakerEnabled = false,
  remoteSpeakerIconColor = 'info',
  onRemoteSpeakerStatusClick,
  remoteSpeakerStatusClickable = false,
  showVisibility = false,
  visibilityEnabled = true,
  onToggleVisibility,
  remoteSpeakerLoading = false,
  visibilityLoading = false,
  disabled = false,
}: ContributorItemInlineStatusIconsProps) {
  const icons = [];
  const actionSpinner = <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />;

  if (showRemoteSpeaker && remoteSpeakerEnabled) {
    const tooltip = contributorRemoteSpeakerStatusIconTooltip(t, {
      slotIsRemote: remoteSpeakerEnabled,
      color: remoteSpeakerStatusClickable ? 'primary' : remoteSpeakerIconColor,
    });
    const icon = (
      <PhoneCallbackOutlined
        fontSize="small"
        color={toMuiIconColor(remoteSpeakerIconColor)}
        sx={{ transform: 'scaleX(-1)' }}
      />
    );

    const clickable = Boolean(remoteSpeakerStatusClickable && onRemoteSpeakerStatusClick);
    icons.push(
      <Tooltip key="remote-speaker-status" title={tooltip} placement="left" disableInteractive>
        <IconButton
          size="small"
          disabled={disabled || remoteSpeakerLoading}
          disableRipple={!clickable}
          tabIndex={clickable ? undefined : -1}
          aria-label={tooltip}
          data-testid="contributor-status-remote-speaker"
          onClick={(e) => {
            e.stopPropagation();
            if (clickable) {
              onRemoteSpeakerStatusClick?.();
            }
          }}
          sx={clickable ? statusIconButtonSx : nonInteractiveIconButtonSx}
        >
          {remoteSpeakerLoading ? actionSpinner : icon}
        </IconButton>
      </Tooltip>,
    );
  }

  if (showVisibility && !visibilityEnabled && onToggleVisibility) {
    const tooltip = contributorVisibilityActionLabel(t, labelContext, visibilityEnabled);
    icons.push(
      <Tooltip key="visibility-status" title={tooltip} placement="left" disableInteractive>
        <IconButton
          size="small"
          disabled={disabled || visibilityLoading}
          aria-label={tooltip}
          data-testid="contributor-status-hidden"
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility();
          }}
          sx={statusIconButtonSx}
        >
          {visibilityLoading ? actionSpinner : <VisibilityOffOutlined fontSize="small" color="info" />}
        </IconButton>
      </Tooltip>,
    );
  }

  return icons;
}

/** @deprecated Use contributorItemInlineStatusIcons */
export const ContributorItemInlineStatusIcons = contributorItemInlineStatusIcons;
